import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Reveal from "./Reveal";
import { useBooking } from "../context/BookingContext";
import { categories, closingStatement } from "../data/servicesData";

const ease = [0.25, 1, 0.5, 1];
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const ServiceRow = ({ service, testId, open, onEnter, onLeave, onToggle }) => (
  <li
    data-testid={`service-${testId}`}
    onMouseEnter={onEnter}
    onMouseLeave={onLeave}
    onClick={onToggle}
    className="group cursor-pointer border-b border-[#FDFBF7]/10 py-4 md:py-5 last:border-b-0"
  >
    <div className="flex items-end gap-3 md:gap-4">
      <span
        data-testid={`service-name-${testId}`}
        className={`font-light text-sm md:text-base transition-colors duration-300 ${
          open ? "text-champagne" : "text-[#FDFBF7]/90 group-hover:text-champagne"
        }`}
      >
        {service.name}
      </span>
      <span className="flex-1 border-b border-dotted border-[#FDFBF7]/25 translate-y-[-4px]" />
      {service.onConsult ? (
        <span
          data-testid={`service-price-${testId}`}
          className="shrink-0 text-[10px] md:text-[11px] uppercase tracking-[0.14em] border border-champagne/50 text-champagne px-3 py-1 rounded-full"
        >
          On Consultation
        </span>
      ) : (
        <span
          data-testid={`service-price-${testId}`}
          className="shrink-0 font-serif text-base md:text-lg font-light text-[#FDFBF7]"
        >
          {service.price}
        </span>
      )}
    </div>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease }}
          className="overflow-hidden"
        >
          <p
            data-testid={`service-desc-${testId}`}
            className="pt-3 pr-4 md:pr-10 text-[13px] md:text-sm text-[#FDFBF7]/55 font-light leading-relaxed max-w-md"
          >
            {service.desc}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </li>
);

export const Services = () => {
  const { openBooking } = useBooking();
  const [activeId, setActiveId] = useState(categories[0].id);
  const [openService, setOpenService] = useState(null);
  const active = categories.find((c) => c.id === activeId);
  const tabRefs = useRef({});

  const handleTabChange = (id) => {
    setActiveId(id);
    setOpenService(null);
    tabRefs.current[id]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  return (
    <section
      id="artistry"
      data-testid="services-section"
      className="bg-[#111111] text-[#FDFBF7] py-28 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl mb-14 md:mb-20">
          <p className="text-champagne text-xs uppercase tracking-[0.3em] mb-5">The Offerings</p>
          <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05]">
            A discipline of detail
          </h2>
          <p className="mt-6 text-[#FDFBF7]/50 text-sm md:text-base font-light">
            Hover a service to reveal its description — on mobile, simply tap.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,280px)_1fr] gap-10 lg:gap-16">
          {/* Numbered chapter rail */}
          <div
            data-testid="service-tabs"
            className="flex lg:flex-col gap-1 lg:gap-1 overflow-x-auto lg:overflow-visible -mx-6 px-6 lg:mx-0 lg:px-0 pb-3 lg:pb-0 scrollbar-hide lg:sticky lg:top-28 lg:self-start"
          >
            {categories.map((cat) => {
              const isActive = cat.id === activeId;
              return (
                <button
                  key={cat.id}
                  ref={(el) => (tabRefs.current[cat.id] = el)}
                  data-testid={`service-tab-${cat.id}`}
                  onClick={() => handleTabChange(cat.id)}
                  className={`relative flex items-baseline gap-3 md:gap-4 shrink-0 text-left py-3 lg:py-3.5 px-4 lg:px-5 border-b-2 lg:border-b-0 lg:border-l-2 transition-colors duration-500 whitespace-nowrap ${
                    isActive ? "border-champagne" : "border-[#FDFBF7]/10 hover:border-[#FDFBF7]/30"
                  }`}
                >
                  <span
                    className={`font-serif text-sm transition-colors duration-500 ${
                      isActive ? "text-champagne" : "text-[#FDFBF7]/55"
                    }`}
                  >
                    {cat.number}
                  </span>
                  <span
                    className={`text-xs md:text-sm uppercase tracking-[0.14em] font-light transition-colors duration-500 ${
                      isActive ? "text-[#FDFBF7]" : "text-[#FDFBF7]/55"
                    }`}
                  >
                    {cat.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active panel */}
          <div className="min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                data-testid={`service-panel-${active.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease }}
              >
                <p className="text-champagne/90 text-xs uppercase tracking-[0.28em] mb-3">
                  {active.eyebrow}
                </p>
                <h3 className="font-serif font-light text-2xl md:text-4xl mb-4 pb-6 border-b border-[#FDFBF7]/15">
                  {active.title}
                </h3>
                {active.intro && (
                  <p className="text-[#FDFBF7]/55 text-sm md:text-base font-light mb-8 -mt-1 max-w-lg">
                    {active.intro}
                  </p>
                )}

                <div className="space-y-10">
                  {active.sections.map((section, si) => (
                    <div key={section.label || si}>
                      {section.label && (
                        <p className="text-[#FDFBF7]/70 font-serif italic text-lg mb-3">
                          {section.label}
                        </p>
                      )}
                      <ul>
                        {section.services.map((service) => {
                          const testId = `${active.id}${section.label ? `-${slug(section.label)}` : ""}-${slug(
                            service.name
                          )}`;
                          const isOpen = openService === testId;
                          const handleToggle = () => {
                            if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
                            setOpenService(isOpen ? null : testId);
                          };
                          return (
                            <ServiceRow
                              key={testId}
                              service={service}
                              testId={testId}
                              open={isOpen}
                              onEnter={() => setOpenService(testId)}
                              onLeave={() => setOpenService(null)}
                              onToggle={handleToggle}
                            />
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>

                {active.highlight && (
                  <p className="mt-8 text-champagne/90 font-serif italic text-base md:text-lg">
                    {active.highlight}
                  </p>
                )}
                {active.note && (
                  <p className="mt-6 text-[#FDFBF7]/40 text-xs md:text-[13px] font-light leading-relaxed max-w-xl">
                    {active.note}
                  </p>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <Reveal className="mt-20 md:mt-24 text-center border-t border-[#FDFBF7]/10 pt-14 md:pt-16">
          <p
            data-testid="services-closing-statement"
            className="font-serif italic text-xl md:text-2xl text-[#FDFBF7]/80 max-w-2xl mx-auto leading-snug mb-10"
          >
            {closingStatement}
          </p>
          <button
            data-testid="services-book-button"
            onClick={() => openBooking()}
            className="inline-flex items-center gap-3 border border-[#FDFBF7]/40 px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#FDFBF7] hover:text-obsidian transition-all duration-500"
          >
            Reserve Your Experience →
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default Services;
